import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { GlassButton } from '../ui/GlassComponents';

const AuthLayout = () => {
  const { darkMode, toggleTheme } = useTheme();
  const location = useLocation();
  
  /// Check if current page is login (both root and /login paths) - if so, use full width, otherwise center
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  const shouldCenter = !isLoginPage;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 animate-fade-in">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-400/20 to-purple-400/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-primary-400/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Header - Hidden for login page */}
      {!isLoginPage && (
        <header className="relative z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-b border-white/20 dark:border-gray-700/20 shadow-glass">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center animate-slide-down">
              <div className="h-10 w-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow-primary mr-3">
                <img 
                  src="/images/lwlogo.png" 
                  alt="LeaveWise Logo" 
                  className="h-6 w-6"
                />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">
                LeaveWise
              </h1>
            </div>
            <GlassButton
              onClick={toggleTheme}
              variant="secondary"
              size="sm"
              className="!p-2 !rounded-full"
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </GlassButton>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={`relative z-10 ${isLoginPage ? 'flex-grow' : 'flex-grow'}`}>
        {shouldCenter ? (
          <div className="flex items-center justify-center min-h-full py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
              <Outlet />
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Footer - Hidden for login page */}
      {!isLoginPage && (
        <footer className="relative z-10 bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg border-t border-white/20 dark:border-gray-700/20 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              &copy; {new Date().getFullYear()} LeaveWise. All rights reserved.
            </p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default AuthLayout;
